import {
  Connection,
  PublicKey,
  Transaction,
  TransactionInstruction,
  SystemProgram,
  SYSVAR_RENT_PUBKEY,
  AccountMeta,
} from '@solana/web3.js';
import {
  Program,
  AnchorProvider,
  BN,
  BorshAccountsCoder,
  BorshInstructionCoder,
} from '@coral-xyz/anchor';
import {
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
} from '@solana/spl-token';
import idl from '../../idl/lixinft_solana.json';
import { solanaPDA, groupIdToBN } from './pda';
import {
  BlockhashHash,
  buildClaimLixiAccounts,
  buildClaimLixiByOwnerAccounts,
  buildClaimLixiByAdminAccounts,
  buildClaimManyByOwnerAccounts,
  buildClaimManyByAdminAccounts,
  buildClaimFundBackAccounts,
  buildAddWhitelisterAccounts,
  buildRemoveWhitelisterAccounts,
  buildAddManyWhitelistersAccounts,
  buildRemoveManyWhitelistersAccounts,
  buildClaimLocalJackpotAccounts,
  buildClaimGlobalJackpotAccounts,
} from './accountBuilders';
import { SOLANA_PROGRAM_ID, SOLANA_RPC_URL } from '../../constants';
import {
  TOKEN_METADATA_PROGRAM_ID,
  getCollectionMetadataPDA,
  getCollectionMasterEditionPDA,
  getCollectionAuthorityRecordPDA,
} from '../../utils/solana-collection';
import { Buffer } from 'buffer';

// Enhanced error handling for IDL parsing
// REMOVED: createFallbackProgram function - we should throw errors instead of returning fake program
// This prevents confusion and ensures issues are caught early

// REMOVED: Fallback data functions - we should throw errors instead of returning fake data
// This prevents confusion and ensures issues are caught early

export class SolanaProgramClient {
  private connection: Connection;
  private program: any;
  private programId: PublicKey;
  private isInitialized = false;

  constructor(rpcUrl?: string, programId?: string) {
    this.connection = new Connection(rpcUrl || SOLANA_RPC_URL, 'confirmed');
    this.programId = new PublicKey(programId || SOLANA_PROGRAM_ID);

    this.initializeProgram();
  }

  private initializeProgram() {
    try {
      // Initialize program with dummy provider for read operations
      const dummyWallet = {
        publicKey: PublicKey.default,
        signTransaction: async (tx: Transaction) => tx,
        signAllTransactions: async (txs: Transaction[]) => txs,
      };
      const provider = new AnchorProvider(
        this.connection,
        dummyWallet as any,
        {},
      );

      // Check if IDL has proper types
      const effectiveIdl: any = idl as any;
      if (!effectiveIdl.types || effectiveIdl.types.length === 0) {
        throw new Error(
          'IDL has empty types array. Cannot initialize program without valid IDL structure. ' +
            'Please ensure the IDL file is properly loaded and contains type definitions.',
        );
      }

      // Try to initialize program with IDL and explicit ProgramId
      // Remove accounts array completely to avoid AccountClient errors
      // We'll use the original IDL (with accounts) for instruction encoding when needed
      const idlForProgram = JSON.parse(JSON.stringify(effectiveIdl));

      // Remove accounts array to prevent AccountClient from trying to process accounts
      // AccountClient requires both type AND size, and we can't reliably calculate size
      // Instruction encoding doesn't need accounts array - it uses instruction definitions directly
      if (idlForProgram.accounts) {
        console.log(
          `⚠️ Removing accounts array (${idlForProgram.accounts.length} accounts) to avoid AccountClient errors`,
        );
        delete idlForProgram.accounts;
      }

      // Try to initialize program with on-chain IDL and explicit ProgramId
      this.program = new (Program as any)(
        idlForProgram,
        new PublicKey(SOLANA_PROGRAM_ID),
        provider,
      );
      this.isInitialized = true;
      console.log('✅ Program initialized with IDL');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      throw new Error(
        `Failed to initialize program with IDL: ${errorMessage}. ` +
          'This is a critical error. Please ensure the IDL file is valid and properly formatted.',
      );
    }
  }

  private normalizeGroupIdValue(value: any): number | string {
    try {
      if (typeof value === 'number') {
        return value;
      }
      if (typeof value === 'bigint') {
        return value <= BigInt(Number.MAX_SAFE_INTEGER)
          ? Number(value)
          : value.toString();
      }
      if (typeof value === 'string') {
        const bigIntValue = BigInt(value);
        return bigIntValue <= BigInt(Number.MAX_SAFE_INTEGER)
          ? Number(bigIntValue)
          : value;
      }
      if (value && typeof value.toString === 'function') {
        const asString = value.toString();
        if (asString && /^[0-9]+$/.test(asString)) {
          const bigIntValue = BigInt(asString);
          return bigIntValue <= BigInt(Number.MAX_SAFE_INTEGER)
            ? Number(bigIntValue)
            : asString;
        }
      }
    } catch (error) {
      console.warn(
        '[SolanaProgramClient] normalizeGroupIdValue failed:',
        error,
      );
    }

    return value;
  }

  private enrichGroupState(state: any, pda: PublicKey, rawData?: Buffer): any {
    const result =
      state && typeof state === 'object' ? { ...state } : ({} as any);

    const convertNumericField = (value: any): number | string | undefined => {
      if (value === null || value === undefined) {
        return undefined;
      }
      if (typeof value === 'number') {
        return value;
      }
      if (typeof value === 'bigint') {
        return Number(value);
      }
      if (value && typeof value.toNumber === 'function') {
        try {
          return value.toNumber();
        } catch {
          // fallthrough to string parsing
        }
      }
      if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isNaN(parsed) ? value : parsed;
      }
      if (value && typeof value.toString === 'function') {
        const str = value.toString();
        const parsed = Number(str);
        return Number.isNaN(parsed) ? str : parsed;
      }
      return undefined;
    };

    const candidate =
      result.groupId !== undefined && result.groupId !== null
        ? result.groupId
        : result.group_id;

    if (candidate !== undefined && candidate !== null) {
      const normalized = this.normalizeGroupIdValue(candidate);
      result.groupId = normalized;
      result.group_id = normalized;
      try {
        result.groupIdString = String(normalized);
      } catch {
        result.groupIdString = String(normalized);
      }
    }

    if (rawData) {
      result._raw = rawData;
    }

    const startTicketCandidate =
      result.startTicketId ?? result.start_ticket_id;
    const claimedCandidate = result.claimed ?? result.claimed_tickets;

    const normalizedStartTicket = convertNumericField(startTicketCandidate);
    if (normalizedStartTicket !== undefined) {
      result.startTicketId = normalizedStartTicket;
      result.start_ticket_id = normalizedStartTicket;
    }

    const normalizedClaimed = convertNumericField(claimedCandidate);
    if (normalizedClaimed !== undefined) {
      result.claimed = normalizedClaimed;
      result.claimed_tickets = normalizedClaimed;
    }

    result._pda = pda;
    return result;
  }

  private manualDecodeGroupState(data: Buffer): any | null {
    // Need at least 88 bytes: discriminator (8) + padding (8) + group_id (8) + owner (32) + size (4) + claimed (4) + hash (16) + start_ticket_id (8)
    if (!data || data.length < 88) {
      return null;
    }

    // Anchor accounts have 8-byte discriminator at the start
    // From hex dump analysis, there appears to be 8 bytes of padding after discriminator:
    // 0-7: discriminator (8 bytes)
    // 8-15: padding (8 bytes) - possibly due to alignment or Anchor serialization
    // 16-23: group_id (u64) - ACTUAL OFFSET in raw data
    // 24-55: owner (Pubkey, 32 bytes)
    // 56-59: size (u32)
    // 60-63: claimed (u32)
    // 64-79: hash ([u8; 16])
    // 72-79: start_ticket_id (u64) - from hex dump analysis
    const DISCRIMINATOR_SIZE = 8;
    const PADDING_SIZE = 8;
    const GROUP_ID_OFFSET = DISCRIMINATOR_SIZE + PADDING_SIZE; // 16
    const OWNER_OFFSET = GROUP_ID_OFFSET + 8; // 24
    const START_TICKET_ID_OFFSET = 72; // From hex dump

    const dv = new DataView(data.buffer, data.byteOffset, data.byteLength);

    // Debug: Log raw bytes at key offsets
    const discriminatorBytes = Array.from(data.slice(0, DISCRIMINATOR_SIZE));
    const groupIdBytes = Array.from(
      data.slice(GROUP_ID_OFFSET, GROUP_ID_OFFSET + 8),
    );
    const ownerBytes = Array.from(data.slice(OWNER_OFFSET, OWNER_OFFSET + 32));
    const startTicketIdBytes = Array.from(
      data.slice(START_TICKET_ID_OFFSET, START_TICKET_ID_OFFSET + 8),
    );

    // Convert bytes to hex for easier debugging
    const groupIdHex = Array.from(groupIdBytes)
      .map(b => b.toString(16).padStart(2, '0'))
      .join(' ');
    const startTicketIdHex = Array.from(startTicketIdBytes)
      .map(b => b.toString(16).padStart(2, '0'))
      .join(' ');

    // Debug: Print first 80 bytes to see full layout
    const first80Bytes = Array.from(data.slice(0, 80))
      .map(b => b.toString(16).padStart(2, '0'))
      .join(' ');

    console.log('[manualDecodeGroupState] Raw bytes debug:', {
      dataLength: data.length,
      first80BytesHex: first80Bytes,
      discriminator: discriminatorBytes
        .map(b => b.toString(16).padStart(2, '0'))
        .join(' '),
      groupIdBytes: groupIdBytes,
      groupIdHex: groupIdHex,
      ownerStart: ownerBytes
        .slice(0, 4)
        .map(b => b.toString(16).padStart(2, '0'))
        .join(' '),
      startTicketIdBytes: startTicketIdBytes,
      startTicketIdHex: startTicketIdHex,
      offsets: {
        GROUP_ID_OFFSET,
        OWNER_OFFSET,
        START_TICKET_ID_OFFSET,
      },
    });

    // Read group_id at offset 8 (after discriminator)
    const groupIdRaw = dv.getBigUint64(GROUP_ID_OFFSET, true);
    // Read start_ticket_id at offset 72 (after discriminator + group_id + owner + size + claimed + hash)
    const startTicketIdRaw = dv.getBigUint64(START_TICKET_ID_OFFSET, true);

    console.log('[manualDecodeGroupState] Decoded values:', {
      groupIdRaw: groupIdRaw.toString(),
      startTicketIdRaw: startTicketIdRaw.toString(),
    });

    const groupId =
      groupIdRaw <= BigInt(Number.MAX_SAFE_INTEGER)
        ? Number(groupIdRaw)
        : groupIdRaw.toString();
    const startTicketId =
      startTicketIdRaw <= BigInt(Number.MAX_SAFE_INTEGER)
        ? Number(startTicketIdRaw)
        : startTicketIdRaw.toString();

    return {
      groupId,
      group_id: groupId,
      startTicketId,
      start_ticket_id: startTicketId,
    };
  }

  /**
   * Get program instance with wallet for write operations
   */
  getProgramWithWallet(wallet: any): any {
    if (!this.isInitialized) {
      throw new Error(
        'Program not initialized. Cannot create program instance without valid IDL. ' +
          'Please ensure initialize() has been called successfully.',
      );
    }

    try {
      const provider = new AnchorProvider(this.connection, wallet, {});

      // Check if IDL has proper types
      const effectiveIdl: any = idl as any;
      if (!effectiveIdl.types || effectiveIdl.types.length === 0) {
        throw new Error(
          'IDL has empty types array. Cannot create program instance for wallet operations without valid IDL structure. ' +
            'Please ensure the IDL file is properly loaded and contains type definitions.',
        );
      }

      // Remove accounts array completely to avoid AccountClient errors
      // We'll use the original IDL (with accounts) for instruction encoding when needed
      const idlForProgram = JSON.parse(JSON.stringify(effectiveIdl));

      // Remove accounts array to prevent AccountClient from trying to process accounts
      // AccountClient requires both type AND size, and we can't reliably calculate size
      // Instruction encoding doesn't need accounts array - it uses instruction definitions directly
      if (idlForProgram.accounts) {
        console.log(
          `⚠️ Removing accounts array (${idlForProgram.accounts.length} accounts) to avoid AccountClient errors`,
        );
        delete idlForProgram.accounts;
      }

      return new (Program as any)(
        idlForProgram,
        new PublicKey(SOLANA_PROGRAM_ID),
        provider,
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      throw new Error(
        `Failed to create program instance with wallet: ${errorMessage}. ` +
          'This is a critical error. Please ensure the IDL file is valid and the wallet is properly connected.',
      );
    }
  }

  /**
   * Get connection
   */
  getConnection(): Connection {
    return this.connection;
  }

  /**
   * Get program ID
   */
  getProgramId(): PublicKey {
    return this.programId;
  }

  /**
   * Get global state account data
   */
  async getGlobalState(): Promise<any> {
    // Always decode from account data to avoid Anchor-IDL construction pitfalls
    const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
    try {
      const accountInfo = await this.connection.getAccountInfo(globalStatePDA);

      if (!accountInfo || !accountInfo.data) {
        throw new Error(
          `Global state account not found or has no data at PDA: ${globalStatePDA.toString()}. ` +
            `This indicates the program may not be initialized or the PDA derivation is incorrect.`,
        );
      }

      // Try decoding via BorshAccountsCoder with the on-chain IDL first
      try {
        const coder = new BorshAccountsCoder(idl as any);
        const tryNames = ['GlobalState', 'globalState'];
        for (const name of tryNames) {
          try {
            const decoded = coder.decode(name as any, accountInfo.data);
            if (decoded && typeof decoded === 'object') {
              return decoded;
            }
          } catch (_) {
            // try next
          }
        }
      } catch (coderErr) {
        console.warn(
          'BorshAccountsCoder decode failed, falling back to raw offsets:',
          coderErr,
        );
      }

      const data = accountInfo.data; // Buffer
      if (data.length < 178) {
        throw new Error(
          `Global state account data too small: ${data.length} bytes (expected at least 178 bytes). ` +
            `This indicates the account may be corrupted or not properly initialized.`,
        );
      }

      // Anchor layout with discriminator (8 bytes)
      // GlobalState struct layout (per IDL):
      // 0..8: discriminator (8 bytes)
      // 8..40: admin (pubkey, 32 bytes)
      // 40..72: collection_mint (pubkey, 32 bytes)
      // 72..80: ticket_price_lamports (u64, 8 bytes)
      // 80..88: closing_time (i64, 8 bytes)
      // 88..96: admin_fee_lamports (u64, 8 bytes)
      // 96..104: init_global_jackpot_lamports (u64, 8 bytes)
      // 104..106: global_jackpot_commission_bps (u16, 2 bytes)
      // 106..108: global_jackpot_service_fee_bps (u16, 2 bytes)
      // 108..110: local_jackpot_percent_bps (u16, 2 bytes)
      // 110..112: global_jackpot_percent_bps (u16, 2 bytes)
      // 112..120: total_tickets (u64, 8 bytes)
      // 120..128: next_ticket_id (u64, 8 bytes)
      // 128..136: next_group_id (u64, 8 bytes)
      const dv = new DataView(data.buffer, data.byteOffset, data.byteLength);
      const ADMIN_OFFSET = 8; // 8..40
      const COLLECTION_MINT_OFFSET = 40; // 40..72
      const TICKET_PRICE_OFFSET = 72; // 72..80
      const CLOSING_TIME_OFFSET = 80; // 80..88
      const NEXT_GROUP_ID_OFFSET = 128; // 128..136

      const adminBytes = data.subarray(ADMIN_OFFSET, ADMIN_OFFSET + 32);
      const admin = new PublicKey(adminBytes);
      const collectionMintBytes = data.subarray(
        COLLECTION_MINT_OFFSET,
        COLLECTION_MINT_OFFSET + 32,
      );
      const collectionMint = new PublicKey(collectionMintBytes);
      const ticketPriceLamports = Number(
        dv.getBigUint64(TICKET_PRICE_OFFSET, true),
      );
      const closingTime = Number(dv.getBigInt64(CLOSING_TIME_OFFSET, true));

      // Decode next_group_id from raw data
      let nextGroupId: number | undefined;
      if (data.length >= 136) {
        try {
          const nextGroupIdRaw = dv.getBigUint64(NEXT_GROUP_ID_OFFSET, true);
          nextGroupId = Number(nextGroupIdRaw);
        } catch (err) {
          console.warn('Failed to decode next_group_id from raw offset:', err);
        }
      }

      // Return a normalized object with both snake_case and camelCase for safety
      return {
        admin,
        adminPubkey: admin,
        ticket_price_lamports: ticketPriceLamports,
        ticketPriceLamports,
        closing_time: closingTime,
        closingTime,
        collection_mint: collectionMint,
        collectionMint,
        next_group_id: nextGroupId,
        nextGroupId,
        // raw passthrough
        _pda: globalStatePDA,
        _dataLen: data.length,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      throw new Error(
        `Failed to get global state from PDA ${globalStatePDA.toString()}: ${errorMessage}. ` +
          `This is a critical error and should be investigated.`,
      );
    }
  }

  /**
   * Get group state account data
   *
   * NOTE: This method uses the deprecated `getGroupStatePDA(groupId)` pattern for backward compatibility.
   * This is kept for querying existing groups that may have been created with the old pattern.
   * For new groups created with buy_tickets_auto, use `getGroupStateByBuyerAndBlockhash` instead.
   *
   * @param groupId - Can be number or bigint (supports large numbers > Number.MAX_SAFE_INTEGER)
   */
  async getGroupState(groupId: number | bigint): Promise<any> {
    // Derive PDA first so it's available in catch block
    // NOTE: Using deprecated getGroupStatePDA for backward compatibility with old groups
    const [groupStatePDA] = solanaPDA.getGroupStatePDA(groupId);
    try {
      // Try to use program account fetch first
      // Try both camelCase and PascalCase account names (Anchor can use either)
      const accountNames = ['groupState', 'GroupState'];

      for (const accountName of accountNames) {
        if (this.program?.account?.[accountName]?.fetch) {
          try {
            const state = await this.program.account[accountName].fetch(
              groupStatePDA,
            );
            // Validate that we got valid data
            if (state && typeof state === 'object') {
              // Check if state has required fields
              if (
                state.groupId !== undefined ||
                state.group_id !== undefined ||
                state.owner !== undefined
              ) {
                return this.enrichGroupState(state, groupStatePDA);
              }
            }
          } catch (error: any) {
            // Handle specific BN-related errors and other decode errors
            const errorMessage =
              error?.message || error?.toString() || 'Unknown error';
            if (
              errorMessage.includes(
                "Cannot read properties of undefined (reading '_bn')",
              ) ||
              errorMessage.includes("'_bn'") ||
              errorMessage.includes('BN') ||
              errorMessage.includes('decode')
            ) {
              // This is a known issue - likely IDL mismatch or missing/optional fields
              // Continue to try next account name or fallback
              if (accountName === accountNames[accountNames.length - 1]) {
                console.warn(
                  `Failed to decode group state using ${accountName} for group ${groupId} (likely IDL mismatch or missing fields), trying manual decode:`,
                  errorMessage.substring(0, 200),
                );
              }
              continue; // Try next account name
            } else {
              // For other errors, log and continue
              console.warn(
                `Failed to fetch group state using ${accountName} for group ${groupId}, trying next method:`,
                errorMessage.substring(0, 200),
              );
              continue;
            }
          }
        }
      }

      // Fallback: try to get account info directly and manually decode
      const accountInfo = await this.connection.getAccountInfo(groupStatePDA);

      if (!accountInfo || !accountInfo.data) {
        throw new Error(
          `Group state account not found or has no data for group ${groupId} at PDA: ${groupStatePDA.toString()}. ` +
            `This indicates the group may not exist or the PDA derivation is incorrect.`,
        );
      }

      // Try to manually decode using BorshAccountsCoder with on-chain IDL
      let lastDecodeError: string | undefined;
      try {
        const coder = new BorshAccountsCoder(idl as any);
        const decodeAccountNames = ['GroupState', 'groupState'];
        for (const decodeAccountName of decodeAccountNames) {
          try {
            const decoded = coder.decode(
              decodeAccountName as any,
              accountInfo.data,
            );
            if (decoded && typeof decoded === 'object') {
              if (
                decoded.groupId !== undefined ||
                decoded.group_id !== undefined ||
                decoded.owner !== undefined
              ) {
                return this.enrichGroupState(
                  decoded,
                  groupStatePDA,
                  accountInfo.data,
                );
              }
            }
          } catch (_) {
            // try next
          }
        }
      } catch (decodeError: any) {
        console.warn(
          `Coder decode failed for group ${groupId}:`,
          decodeError?.message?.substring(0, 200) || decodeError,
        );
        lastDecodeError = decodeError?.message || String(decodeError);
      }

      const manualDecoded = this.manualDecodeGroupState(accountInfo.data);
      if (manualDecoded) {
        return this.enrichGroupState(
          manualDecoded,
          groupStatePDA,
          accountInfo.data,
        );
      }

      // If we reach here, decoding failed but no error was thrown - this shouldn't happen
      throw new Error(
        `Group state account exists at PDA ${groupStatePDA.toString()} but cannot be decoded for group ${groupId}. ` +
          `Account data length: ${accountInfo.data.length} bytes. ` +
          `This indicates the account may be corrupted or the IDL structure has changed.` +
          (lastDecodeError ? ` Last decode error: ${lastDecodeError}` : ''),
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      throw new Error(
        `Failed to get group state for group ${groupId} from PDA ${groupStatePDA.toString()}: ${errorMessage}. ` +
          `This is a critical error and should be investigated.`,
      );
    }
  }

  /**
   * Get group state account data by buyer and blockhash_hash
   * This method is specifically for buy_tickets_auto flow which uses PDA pattern: [b"group", buyer, blockhash_hash]
   * @param buyer - Buyer's public key
   * @param blockhashHash - First 4 bytes of hashed recent_blockhash (u32) as string, bigint, or number
   */
  async getGroupStateByBuyerAndBlockhash(
    buyer: PublicKey,
    blockhashHash: string | number | bigint,
  ): Promise<any> {
    // Derive PDA using buy_tickets_auto pattern
    const [groupStatePDA] = solanaPDA.getGroupStatePDAForAuto(
      buyer,
      blockhashHash,
    );
    try {
      // Try to use program account fetch first
      const accountNames = ['groupState', 'GroupState'];

      for (const accountName of accountNames) {
        if (this.program?.account?.[accountName]?.fetch) {
          try {
            const state = await this.program.account[accountName].fetch(
              groupStatePDA,
            );
            if (state && typeof state === 'object') {
              if (
                state.groupId !== undefined ||
                state.group_id !== undefined ||
                state.owner !== undefined
              ) {
                return this.enrichGroupState(state, groupStatePDA);
              }
            }
          } catch (error: any) {
            const errorMessage =
              error?.message || error?.toString() || 'Unknown error';
            if (
              errorMessage.includes("'_bn'") ||
              errorMessage.includes('BN') ||
              errorMessage.includes('decode')
            ) {
              if (accountName === accountNames[accountNames.length - 1]) {
                console.warn(
                  `Failed to decode group state using ${accountName} for buyer ${buyer.toString()} and blockhashHash ${blockhashHash}, trying manual decode:`,
                  errorMessage.substring(0, 200),
                );
              }
              continue;
            } else {
              console.warn(
                `Failed to fetch group state using ${accountName} for buyer ${buyer.toString()} and blockhashHash ${blockhashHash}, trying next method:`,
                errorMessage.substring(0, 200),
              );
              continue;
            }
          }
        }
      }

      // Fallback: try to get account info directly and manually decode
      const accountInfo = await this.connection.getAccountInfo(groupStatePDA);

      if (!accountInfo || !accountInfo.data) {
        throw new Error(
          `Group state account not found or has no data for buyer ${buyer.toString()} and blockhashHash ${blockhashHash} at PDA: ${groupStatePDA.toString()}. ` +
            `This indicates the group may not exist or the PDA derivation is incorrect.`,
        );
      }

      // Try to manually decode using BorshAccountsCoder with on-chain IDL
      let lastDecodeError: string | undefined;
      try {
        const coder = new BorshAccountsCoder(idl as any);
        const decodeAccountNames = ['GroupState', 'groupState'];
        for (const decodeAccountName of decodeAccountNames) {
          try {
            const decoded = coder.decode(
              decodeAccountName as any,
              accountInfo.data,
            );
            if (decoded && typeof decoded === 'object') {
              console.log(
                `[getGroupStateByBuyerAndBlockhash] BorshAccountsCoder decoded (${decodeAccountName}):`,
                {
                  groupId: decoded.groupId ?? decoded.group_id,
                  groupIdType: typeof (decoded.groupId ?? decoded.group_id),
                  owner: decoded.owner?.toString?.() ?? decoded.owner,
                  startTicketId:
                    decoded.startTicketId ?? decoded.start_ticket_id,
                },
              );
              if (
                decoded.groupId !== undefined ||
                decoded.group_id !== undefined ||
                decoded.owner !== undefined
              ) {
                return this.enrichGroupState(
                  decoded,
                  groupStatePDA,
                  accountInfo.data,
                );
              }
            }
          } catch (_) {
            // try next
          }
        }
      } catch (decodeError: any) {
        console.warn(
          `Coder decode failed for buyer ${buyer.toString()} and blockhashHash ${blockhashHash}:`,
          decodeError?.message?.substring(0, 200) || decodeError,
        );
        lastDecodeError = decodeError?.message || String(decodeError);
      }

      const manualDecoded = this.manualDecodeGroupState(accountInfo.data);
      if (manualDecoded) {
        return this.enrichGroupState(
          manualDecoded,
          groupStatePDA,
          accountInfo.data,
        );
      }

      // If we reach here, decoding failed but no error was thrown - this shouldn't happen
      throw new Error(
        `Group state account exists at PDA ${groupStatePDA.toString()} but cannot be decoded for buyer ${buyer.toString()} and blockhashHash ${blockhashHash}. ` +
          `Account data length: ${accountInfo.data.length} bytes. ` +
          `This indicates the account may be corrupted or the IDL structure has changed.` +
          (lastDecodeError ? ` Last decode error: ${lastDecodeError}` : ''),
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      throw new Error(
        `Failed to get group state for buyer ${buyer.toString()} and blockhashHash ${blockhashHash} from PDA ${groupStatePDA.toString()}: ${errorMessage}. ` +
          `This is a critical error and should be investigated.`,
      );
    }
  }

  // WhitelistPage removed; whitelist is now integrated in GroupState

  /**
   * Check if user is whitelisted
   * @param groupId - Can be number or bigint (supports large numbers > Number.MAX_SAFE_INTEGER)
   */
  async isWhitelisted(
    groupId: number | bigint,
    user: PublicKey,
    groupOptions: {
      blockhashHash: string;
      buyer: PublicKey;
    },
  ): Promise<boolean> {
    try {
      const program = this.getProgramWithWallet({
        publicKey: PublicKey.default,
        signTransaction: async (tx: Transaction) => tx,
        signAllTransactions: async (txs: Transaction[]) => txs,
      } as any);
      const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
      if (!groupOptions?.blockhashHash || !groupOptions?.buyer) {
        throw new Error(
          '[isWhitelisted] Missing blockhashHash hoặc buyer cho buy_tickets_auto PDA',
        );
      }
      const [groupStatePDA] = solanaPDA.getGroupStatePDAForAuto(
        groupOptions.buyer,
        groupOptions.blockhashHash,
      );
      // Call on-chain view (logs only), success implies account layout/args are valid
      await program.methods
        .isWhitelisted(groupIdToBN(groupId), user)
        .accounts({
          globalState: globalStatePDA,
          groupState: groupStatePDA,
        })
        .rpc();
      // If no error thrown, treat as callable. Actual boolean is enforced in claim path.
      return true;
    } catch (error) {
      console.error('Failed to check whitelist status:', error);
      return false;
    }
  }

  /**
   * Get jackpot vault balance
   */
  async getJackpotVaultBalance(): Promise<number> {
    try {
      const [jackpotVaultPDA] = solanaPDA.getJackpotVaultPDA();
      const balance = await this.connection.getBalance(jackpotVaultPDA);
      return balance;
    } catch (error) {
      console.error('Failed to get jackpot vault balance:', error);
      return 0;
    }
  }

  /**
   * Get ticket info (mock implementation for now)
   */
  async getTicketInfo(ticketId: number): Promise<any> {
    try {
      // This would need to be implemented based on how tickets are stored
      // For now, return mock data
      return {
        id: ticketId,
        groupId: 1,
        owner: PublicKey.default,
        isClaimed: false,
        claimTime: 0,
        price: 0.1,
        currency: 'SOL',
      };
    } catch (error) {
      console.error('Failed to get ticket info:', error);
      return null;
    }
  }

  /**
   * Create buy tickets instruction
   */
  async createBuyTicketsInstruction(
    wallet: any,
    params: {
      groupId: number | bigint;
      numTickets: number;
      extraTokenMint?: PublicKey;
      extraTokenAmount: number;
      receivers: PublicKey[];
      // Optional remaining accounts for extra token flow
      buyerATA?: PublicKey;
      vaultAuthority?: PublicKey;
      vaultATA?: PublicKey;
      receiverATAs?: PublicKey[];
      // Optional per-ticket NFT accounts (if needed)
      nftMintPDAs?: PublicKey[];
      nftMetadataPDAs?: PublicKey[];
      nftMasterEditionPDAs?: PublicKey[];
      nftReceiverTokenAccountPDAs?: PublicKey[];
    },
    groupOptions: {
      blockhashHash: string;
      buyer: PublicKey;
    },
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
    if (!groupOptions?.blockhashHash || !groupOptions?.buyer) {
      throw new Error(
        '[createBuyTicketsInstruction] Missing blockhashHash hoặc buyer cho buy_tickets_auto PDA',
      );
    }
    const [groupStatePDA] = solanaPDA.getGroupStatePDAForAuto(
      groupOptions.buyer,
      groupOptions.blockhashHash,
    );
    const [jackpotVaultPDA] = solanaPDA.getJackpotVaultPDA();
    const [collectionMintPDA] = solanaPDA.getCollectionMintPDA();

    // Use PublicKey.default as placeholder for no extra token
    const extraTokenMint = params.extraTokenMint || PublicKey.default;

    // Build remaining accounts if provided
    // According to IDL analysis and flow:
    // - buy_tickets instruction chỉ tạo lixi (tickets), KHÔNG transfer tokens đến receivers
    // - Token transfers đến receivers được thực hiện ở bước riêng (sau buy tickets)
    // - buyer_token_ata IS in IDL accounts (line 629) - add to .accounts() as buyerTokenAta
    // - token_vault_ata IS in IDL accounts (line 633) - this is group_vault_ata
    // - receiver_token_atas cho extra token KHÔNG cần trong buy_tickets instruction
    // - Per-ticket NFT accounts (nếu có) sẽ ở remaining accounts
    // Order for remaining accounts:
    // 1. Per-ticket accounts (nft_mint, nft_metadata, nft_master_edition, receiver_token_account for NFTs)
    const remainingAccounts: any[] = [];

    // NOTE: receiver_token_atas cho extra token KHÔNG được thêm vào đây
    // vì buy_tickets chỉ tạo tickets, không transfer tokens đến receivers
    // Token transfers sẽ được thực hiện ở bước riêng (transferToReceivers)

    // Add per-ticket NFT accounts if provided
    // Order per ticket: nft_mint, nft_metadata, nft_master_edition, receiver_token_account
    if (
      params.nftMintPDAs &&
      params.nftMetadataPDAs &&
      params.nftMasterEditionPDAs &&
      params.nftReceiverTokenAccountPDAs
    ) {
      for (let i = 0; i < params.nftMintPDAs.length; i++) {
        remainingAccounts.push(
          {
            pubkey: params.nftMintPDAs[i],
            isSigner: false,
            isWritable: true,
          },
          {
            pubkey: params.nftMetadataPDAs[i],
            isSigner: false,
            isWritable: true,
          },
          {
            pubkey: params.nftMasterEditionPDAs[i],
            isSigner: false,
            isWritable: true,
          },
          {
            pubkey: params.nftReceiverTokenAccountPDAs[i],
            isSigner: false,
            isWritable: true,
          },
        );
      }
    }

    // Derive collection metadata accounts (authority record will be derived after resolving admin)
    const collectionMetadata = getCollectionMetadataPDA(collectionMintPDA);
    const collectionMasterEdition =
      getCollectionMasterEditionPDA(collectionMintPDA);

    // Validate collection accounts exist (optional check, but helpful for debugging)
    // Note: These accounts may not exist yet if collection hasn't been initialized
    try {
      const connection =
        (program.provider as AnchorProvider)?.connection || this.connection;
      const collectionMetadataInfo = await connection.getAccountInfo(
        collectionMetadata,
      );
      const collectionMasterEditionInfo = await connection.getAccountInfo(
        collectionMasterEdition,
      );

      console.log('[createBuyTicketsInstruction] Collection accounts status:', {
        collectionMint: collectionMintPDA.toString(),
        collectionMetadata: {
          address: collectionMetadata.toString(),
          exists: !!collectionMetadataInfo,
        },
        collectionMasterEdition: {
          address: collectionMasterEdition.toString(),
          exists: !!collectionMasterEditionInfo,
        },
      });

      // Warn if accounts don't exist (may be expected if collection not initialized)
      if (!collectionMetadataInfo) {
        console.warn(
          '[createBuyTicketsInstruction] Collection metadata account does not exist. This may cause transaction to fail if collection is required.',
        );
      }
      if (!collectionMasterEditionInfo) {
        console.warn(
          '[createBuyTicketsInstruction] Collection master edition account does not exist. This may be expected for token-only collections.',
        );
      }
    } catch (checkError) {
      console.warn(
        '[createBuyTicketsInstruction] Could not check collection accounts:',
        checkError,
      );
      // Continue anyway - the transaction will fail with a clearer error if accounts are missing
    }

    // Get admin account - try to fetch from global state, fallback to buyer
    // IMPORTANT: Admin must be a signer. If admin is different from buyer,
    // we need to ensure admin signs the transaction (or use buyer as admin if allowed)
    let admin: PublicKey = wallet.publicKey;
    try {
      // Fetch global state via client to get admin
      const globalStateAccount = await this.getGlobalState();
      if (globalStateAccount && globalStateAccount.admin) {
        const fetchedAdmin = globalStateAccount.admin as PublicKey;

        // If admin is same as buyer, use buyer (who is already a signer)
        if (fetchedAdmin.toString() === wallet.publicKey.toString()) {
          admin = wallet.publicKey;
          console.log(
            '[createBuyTicketsInstruction] Admin matches buyer, using buyer as admin',
          );
        } else {
          // Admin is different from buyer
          // Option 1: Use buyer as admin (if program allows)
          // Option 2: Require admin to sign (would need to pass admin wallet)
          // For now, use buyer as admin if different (may need to adjust based on program requirements)
          console.warn(
            `[createBuyTicketsInstruction] Admin (${fetchedAdmin.toString()}) differs from buyer (${wallet.publicKey.toString()}). Using buyer as admin.`,
          );
          admin = wallet.publicKey; // Use buyer as admin to ensure signer
        }
      }
    } catch (error) {
      console.warn(
        '[createBuyTicketsInstruction] Could not fetch global state, using buyer as admin:',
        error,
      );
      // Use buyer as admin fallback (buyer is always a signer)
    }

    console.log('[createBuyTicketsInstruction] Using admin:', admin.toString());

    // Now that admin is known, derive collectionAuthorityRecord by (collection_mint, admin)
    const collectionAuthorityRecord = getCollectionAuthorityRecordPDA(
      collectionMintPDA,
      admin,
    );

    const instructionBuilder = program.methods
      .buyTickets(
        params.numTickets,
        extraTokenMint,
        new BN(params.extraTokenAmount),
        params.receivers,
      )
      .accounts({
        // Use camelCase keys per Anchor js mapping
        globalState: globalStatePDA,
        groupState: groupStatePDA,
        admin: admin,
        buyer: wallet.publicKey,
        jackpotVault: jackpotVaultPDA,
        collectionMint: collectionMintPDA,
        collectionMetadata: collectionMetadata,
        collectionMasterEdition: collectionMasterEdition,
        collectionAuthorityRecord: collectionAuthorityRecord,
        ...(params.extraTokenMint &&
          params.extraTokenMint.toString() !== PublicKey.default.toString() && {
            extraTokenMintAccount: params.extraTokenMint,
          }),
        ...(params.vaultAuthority && {
          vaultAuthority: params.vaultAuthority,
        }),
        ...(params.vaultATA && {
          groupVaultAta: params.vaultATA,
        }),
        tokenMetadataProgram: TOKEN_METADATA_PROGRAM_ID,
        tokenProgram: TOKEN_PROGRAM_ID,
        associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
        systemProgram: SystemProgram.programId,
        rent: SYSVAR_RENT_PUBKEY,
      });

    // Add remaining accounts if any
    if (remainingAccounts.length > 0) {
      instructionBuilder.remainingAccounts(remainingAccounts);
    }

    return await instructionBuilder.instruction();
  }

  /**
   * Create deposit_group_spl instruction
   * Locks SPL prizes into the group vault (local jackpot flow)
   */
  async createDepositGroupSplInstruction(
    wallet: any,
    params: {
      groupId: number | bigint;
      blockhashHash: string | number | bigint;
      buyer: PublicKey;
      extraTokenMint: PublicKey;
      amount: BN | number | bigint | string;
      buyerTokenAta?: PublicKey;
      vaultAuthority?: PublicKey;
      tokenVaultAta?: PublicKey;
    },
  ): Promise<TransactionInstruction> {
    if (!wallet?.publicKey) {
      throw new Error(
        '[createDepositGroupSplInstruction] Wallet not connected',
      );
    }
    if (!params?.groupId) {
      throw new Error('[createDepositGroupSplInstruction] Missing groupId');
    }
    if (!params?.blockhashHash) {
      throw new Error(
        '[createDepositGroupSplInstruction] Missing blockhashHash',
      );
    }
    if (!params?.extraTokenMint) {
      throw new Error(
        '[createDepositGroupSplInstruction] Missing extraTokenMint',
      );
    }

    const program = this.getProgramWithWallet(wallet);
    const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
    const [groupStatePDA] = solanaPDA.getGroupStatePDAForAuto(
      params.buyer,
      params.blockhashHash,
    );

    let vaultAuthority = params.vaultAuthority;
    if (!vaultAuthority) {
      [vaultAuthority] = solanaPDA.getExtraTokenVaultPDA(
        params.buyer,
        params.blockhashHash,
        params.extraTokenMint,
      );
    }

    let buyerTokenAta = params.buyerTokenAta;
    let tokenVaultAta = params.tokenVaultAta;
    if (!buyerTokenAta || !tokenVaultAta) {
      const { getAssociatedTokenAddress } = await import('@solana/spl-token');
      if (!buyerTokenAta) {
        buyerTokenAta = await getAssociatedTokenAddress(
          params.extraTokenMint,
          params.buyer,
        );
      }
      if (!tokenVaultAta) {
        tokenVaultAta = await getAssociatedTokenAddress(
          params.extraTokenMint,
          vaultAuthority,
          true,
        );
      }
    }

    if (!buyerTokenAta || !tokenVaultAta || !vaultAuthority) {
      throw new Error(
        '[createDepositGroupSplInstruction] Failed to derive SPL accounts',
      );
    }

    let normalizedAmount: number | bigint;
    if (params.amount instanceof BN) {
      normalizedAmount = BigInt(params.amount.toString());
    } else if (typeof params.amount === 'string') {
      normalizedAmount = BigInt(params.amount);
    } else {
      normalizedAmount = (params.amount ?? 0) as number | bigint;
    }
    const amountBn = groupIdToBN(normalizedAmount);

    const accounts = {
      globalState: globalStatePDA,
      groupState: groupStatePDA,
      buyer: wallet.publicKey,
      extraTokenMint: params.extraTokenMint,
      vaultAuthority,
      buyerTokenAta,
      tokenVaultAta,
      tokenProgram: TOKEN_PROGRAM_ID,
      associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
      systemProgram: SystemProgram.programId,
    };

    const method =
      program?.methods?.depositGroupSpl || program?.methods?.deposit_group_spl;
    if (method) {
      try {
        return await method(groupIdToBN(params.groupId), amountBn)
          .accounts(accounts)
          .instruction();
      } catch (encodeError) {
        console.warn(
          '[createDepositGroupSplInstruction] Anchor encode failed, falling back to manual encoding:',
          encodeError,
        );
      }
    } else {
      console.warn(
        '[createDepositGroupSplInstruction] depositGroupSpl not found in program.methods, falling back to manual encoding',
      );
    }

    // Manual fallback using BorshInstructionCoder
    const instructionCoder = new BorshInstructionCoder(idl as any);
    const data = instructionCoder.encode('deposit_group_spl', {
      group_id: groupIdToBN(params.groupId),
      amount: amountBn,
    } as any);

    const keys = [
      { pubkey: globalStatePDA, isSigner: false, isWritable: true },
      { pubkey: groupStatePDA, isSigner: false, isWritable: true },
      { pubkey: wallet.publicKey, isSigner: true, isWritable: true },
      { pubkey: params.extraTokenMint, isSigner: false, isWritable: false },
      { pubkey: vaultAuthority, isSigner: false, isWritable: false },
      { pubkey: buyerTokenAta, isSigner: false, isWritable: true },
      { pubkey: tokenVaultAta, isSigner: false, isWritable: true },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
      {
        pubkey: ASSOCIATED_TOKEN_PROGRAM_ID,
        isSigner: false,
        isWritable: false,
      },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ];

    return new TransactionInstruction({
      programId: this.programId,
      keys,
      data,
    });
  }

  /**
   * Create buy tickets auto instruction
   * Chỉ hàm này dùng pattern mới [b"group", buyer, slot] - không cần group_id
   * Các hàm khác (claim_lixi, addWhitelister, ...) vẫn dùng pattern cũ [b"group", group_id]
   * receivers is optional - can be empty array, receivers can be added later via claim or whitelist
   */
  async createBuyTicketsAutoInstruction(
    wallet: any,
    params: {
      numTickets: number;
      extraTokenMint?: PublicKey;
      extraTokenAmount?: number | bigint | string | BN;
      receivers?: PublicKey[]; // Optional - can be empty
      blockhashHash?: string; // Optional - if provided, use this instead of calculating (as string to avoid precision loss)
    },
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
    const [jackpotVaultPDA] = solanaPDA.getJackpotVaultPDA();

    // Derive collection accounts from global state
    const globalState = await this.getGlobalState();

    // Get blockhash_hash - use provided value or calculate from recent_blockhash
    // Pattern mới: [b"group", buyer, blockhash_hash] - chỉ dùng cho buy_tickets_auto
    // recent_blockhash được đóng băng khi transaction được prepare, đảm bảo tính nhất quán
    let blockhashHashValue: string | bigint;
    if (params.blockhashHash !== undefined) {
      // Use provided blockhash_hash (as string to avoid precision loss)
      blockhashHashValue = params.blockhashHash;
    } else {
      // Calculate from recent_blockhash
      const { blockhash } = await this.connection.getLatestBlockhash(
        'confirmed',
      );

      // Hash recent_blockhash và lấy first 4 bytes làm u32 (giống như Rust)
      // blockhash là base58 string, cần decode thành bytes trước khi hash
      const { sha256 } = await import('@noble/hashes/sha256');
      const { decode } = await import('bs58');
      const blockhashBytes = decode(blockhash);
      const blockhashHash = sha256(blockhashBytes);
      // Convert hash to Buffer và lấy first 4 bytes làm u32
      // sha256 returns Uint8Array, convert to Buffer for readUint32LE
      const blockhashHashBuffer = Buffer.from(blockhashHash);
      // Use DataView for cross-platform compatibility (works in browser and Node.js)
      const dataView = new DataView(
        blockhashHashBuffer.buffer,
        blockhashHashBuffer.byteOffset,
        blockhashHashBuffer.byteLength,
      );
      const blockhashHashU32 = dataView.getUint32(0, true); // true = little endian, u32 (4 bytes)
      // Keep as string to avoid precision loss
      blockhashHashValue = blockhashHashU32.toString();
    }
    console.log('blockhashHashValue', blockhashHashValue);

    // Derive group_state PDA từ buyer + blockhash_hash (pattern mới chỉ cho buy_tickets_auto)
    const [groupStatePDA] = solanaPDA.getGroupStatePDAForAuto(
      wallet.publicKey,
      blockhashHashValue,
    );
    const collectionMintPDA: PublicKey = (globalState.collectionMint ||
      globalState.collection_mint) as PublicKey;

    // Validate collection mint is not default/zero address
    if (!collectionMintPDA || collectionMintPDA.equals(PublicKey.default)) {
      throw new Error(
        'Collection mint is not initialized. Please initialize global state first.',
      );
    }

    const collectionMetadata = getCollectionMetadataPDA(collectionMintPDA);
    const collectionMasterEdition =
      getCollectionMasterEditionPDA(collectionMintPDA);
    // Use admin as collection authority (matches initialize_global)
    const adminPk: PublicKey = (globalState.admin ||
      (globalState as any).adminPubkey) as PublicKey;

    // Validate admin is not default/zero address
    if (!adminPk || adminPk.equals(PublicKey.default)) {
      throw new Error(
        'Admin is not set in global state. Please initialize global state first.',
      );
    }

    const collectionAuthorityRecord = getCollectionAuthorityRecordPDA(
      collectionMintPDA,
      adminPk,
    );

    // Optionally derive vault authority and group vault ATA if extra token present
    let vaultAuthority: PublicKey | undefined;
    let groupVaultAta: PublicKey | undefined;
    let buyerExtraTokenAta: PublicKey | undefined;
    if (
      params.extraTokenMint &&
      params.extraTokenMint.toString() !== PublicKey.default.toString()
    ) {
      // ✅ Vault PDA pattern: [b"vault", buyer, blockhash_hash, mint]
      // This matches Rust program's vault authority derivation for buy_tickets_auto flow
      // Allows deriving vault authority BEFORE buy tickets (same as group_state PDA pattern)
      const [pda] = solanaPDA.getExtraTokenVaultPDA(
        wallet.publicKey,
        blockhashHashValue,
        params.extraTokenMint,
      );
      vaultAuthority = pda;
      try {
        const { getAssociatedTokenAddress } = await import('@solana/spl-token');
        groupVaultAta = await getAssociatedTokenAddress(
          params.extraTokenMint,
          vaultAuthority,
          true, // allow owner off curve
        );
        buyerExtraTokenAta = await getAssociatedTokenAddress(
          params.extraTokenMint,
          wallet.publicKey,
        );
      } catch (err) {
        console.warn(
          '[createBuyTicketsAutoInstruction] Failed to derive SPL ATAs:',
          err,
        );
      }
    }

    // Build instruction manually (stable path to avoid Anchor encode issues)
    const extraMintArg = params.extraTokenMint ? params.extraTokenMint : null;
    // receivers is optional - can be empty array
    const receiversArg = (params.receivers || []) as PublicKey[];

    const discriminator = Uint8Array.from([77, 125, 39, 36, 35, 241, 210, 184]);
    const numTicketsLe = new Uint8Array(4);
    new DataView(numTicketsLe.buffer).setUint32(
      0,
      params.numTickets >>> 0,
      true,
    );
    // Add blockhash_hash parameter (u32, 4 bytes) - used for PDA derivation
    const blockhashHashLe = new Uint8Array(4); // Changed from 8 to 4 bytes for u32
    const blockhashHashLeDataView = new DataView(blockhashHashLe.buffer);
    const blockhashHashNumberForInstruction =
      typeof blockhashHashValue === 'string'
        ? Number(blockhashHashValue)
        : typeof blockhashHashValue === 'bigint'
        ? Number(blockhashHashValue)
        : blockhashHashValue;
    blockhashHashLeDataView.setUint32(
      0,
      blockhashHashNumberForInstruction,
      true,
    ); // true = little endian, u32 (4 bytes)
    const extraMintBuf = new Uint8Array(extraMintArg ? 1 + 32 : 1);
    extraMintBuf[0] = extraMintArg ? 1 : 0;
    if (extraMintArg) {
      extraMintBuf.set((extraMintArg as PublicKey).toBytes(), 1);
    }
    const amountLe = new Uint8Array(8);
    const amtBn = new BN(String(params.extraTokenAmount ?? 0));
    const amtHex = amtBn.toArray('le', 8);
    amountLe.set(Uint8Array.from(amtHex), 0);
    const receiversLenLe = new Uint8Array(4);
    new DataView(receiversLenLe.buffer).setUint32(
      0,
      receiversArg.length >>> 0,
      true,
    );
    const receiverBytes = receiversArg.map(pk => (pk as PublicKey).toBytes());
    const receiversBuf = new Uint8Array(receiverBytes.length * 32);
    for (let i = 0; i < receiverBytes.length; i++) {
      receiversBuf.set(receiverBytes[i], i * 32);
    }
    const data = new Uint8Array(
      discriminator.length +
        numTicketsLe.length +
        blockhashHashLe.length +
        extraMintBuf.length +
        amountLe.length +
        receiversLenLe.length +
        receiversBuf.length,
    );
    let offset = 0;
    data.set(discriminator, offset);
    offset += discriminator.length;
    data.set(numTicketsLe, offset);
    offset += numTicketsLe.length;
    data.set(blockhashHashLe, offset);
    offset += blockhashHashLe.length;
    data.set(extraMintBuf, offset);
    offset += extraMintBuf.length;
    data.set(amountLe, offset);
    offset += amountLe.length;
    data.set(receiversLenLe, offset);
    offset += receiversLenLe.length;
    data.set(receiversBuf, offset);

    // Validate program addresses match IDL
    const IDL_TOKEN_PROGRAM = new PublicKey(
      'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA',
    );
    const IDL_ASSOCIATED_TOKEN_PROGRAM = new PublicKey(
      'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL',
    );
    const IDL_SYSTEM_PROGRAM = SystemProgram.programId; // Same as IDL

    if (!TOKEN_PROGRAM_ID.equals(IDL_TOKEN_PROGRAM)) {
      console.warn(
        '[createBuyTicketsAutoInstruction] TOKEN_PROGRAM_ID mismatch:',
        {
          current: TOKEN_PROGRAM_ID.toString(),
          expected: IDL_TOKEN_PROGRAM.toString(),
        },
      );
    }
    if (!ASSOCIATED_TOKEN_PROGRAM_ID.equals(IDL_ASSOCIATED_TOKEN_PROGRAM)) {
      console.warn(
        '[createBuyTicketsAutoInstruction] ASSOCIATED_TOKEN_PROGRAM_ID mismatch:',
        {
          current: ASSOCIATED_TOKEN_PROGRAM_ID.toString(),
          expected: IDL_ASSOCIATED_TOKEN_PROGRAM.toString(),
        },
      );
    }

    const keys = [
      { pubkey: globalStatePDA, isSigner: false, isWritable: true },
      { pubkey: groupStatePDA, isSigner: false, isWritable: true },
      { pubkey: wallet.publicKey, isSigner: true, isWritable: true },
      { pubkey: jackpotVaultPDA, isSigner: false, isWritable: true },
      { pubkey: collectionMintPDA, isSigner: false, isWritable: false },
      { pubkey: collectionMetadata, isSigner: false, isWritable: false },
      { pubkey: collectionMasterEdition, isSigner: false, isWritable: false },
      {
        pubkey: collectionAuthorityRecord,
        isSigner: false,
        isWritable: true,
      },
    ];

    const remainingAccounts: AccountMeta[] = [];
    const receiverAtas: PublicKey[] = [];

    if (
      params.extraTokenMint &&
      params.extraTokenMint.toString() !== PublicKey.default.toString()
    ) {
      if (!buyerExtraTokenAta || !vaultAuthority || !groupVaultAta) {
        throw new Error(
          '[createBuyTicketsAutoInstruction] Missing SPL accounts for extra token flow',
        );
      }
      remainingAccounts.push(
        {
          pubkey: params.extraTokenMint,
          isSigner: false,
          isWritable: false,
        },
        {
          pubkey: buyerExtraTokenAta,
          isSigner: false,
          isWritable: true,
        },
        {
          pubkey: vaultAuthority,
          isSigner: false,
          isWritable: false,
        },
        {
          pubkey: groupVaultAta,
          isSigner: false,
          isWritable: true,
        },
      );

      if (receiversArg.length > 0) {
        try {
          const { getAssociatedTokenAddress } = await import(
            '@solana/spl-token'
          );
          for (const receiver of receiversArg) {
            const receiverAta = await getAssociatedTokenAddress(
              params.extraTokenMint,
              receiver,
            );
            receiverAtas.push(receiverAta);
            remainingAccounts.push({
              pubkey: receiverAta,
              isSigner: false,
              isWritable: true,
            });
          }
        } catch (receiverAtaError) {
          console.warn(
            '[createBuyTicketsAutoInstruction] Failed to derive receiver ATAs:',
            receiverAtaError,
          );
        }
      }
    }

    keys.push(
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
      {
        pubkey: TOKEN_METADATA_PROGRAM_ID,
        isSigner: false,
        isWritable: false,
      },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: SYSVAR_RENT_PUBKEY, isSigner: false, isWritable: false },
    );

    const combinedKeys = keys.concat(remainingAccounts);

    // Log accounts for debugging (only in dev mode)
    if (process.env.NODE_ENV !== 'production') {
      console.log(
        '[createBuyTicketsAutoInstruction] Accounts (base + remaining):',
        {
          base: {
            global_state: globalStatePDA.toString(),
            group_state: groupStatePDA.toString(),
            buyer: wallet.publicKey.toString(),
            jackpot_vault: jackpotVaultPDA.toString(),
            collection_mint: collectionMintPDA.toString(),
            collection_metadata: collectionMetadata.toString(),
            collection_master_edition: collectionMasterEdition.toString(),
            collection_authority_record: collectionAuthorityRecord.toString(),
            token_program: TOKEN_PROGRAM_ID.toString(),
            token_metadata_program: TOKEN_METADATA_PROGRAM_ID.toString(),
            system_program: SystemProgram.programId.toString(),
            rent: SYSVAR_RENT_PUBKEY.toString(),
          },
          remaining: params.extraTokenMint
            ? {
                extra_token_mint: params.extraTokenMint.toString(),
                buyer_token_ata: buyerExtraTokenAta?.toString(),
                vault_authority: vaultAuthority?.toString(),
                group_vault_ata: groupVaultAta?.toString(),
                receiver_atas: receiverAtas.map(ata => ata.toString()),
              }
            : 'none (native SOL flow)',
          summary: {
            totalAccounts: combinedKeys.length,
            blockhashHash: blockhashHashValue,
            numTickets: params.numTickets,
            receiversCount: receiversArg.length,
            hasExtraToken: !!params.extraTokenMint,
          },
        },
      );
    }

    return new TransactionInstruction({
      programId: this.programId,
      keys: combinedKeys,
      data: Buffer.from(data),
    });
  }

  /**
   * Create transfer_extra_tokens instruction
   * Moves buyer's SPL tokens into the vault ATA after buy_tickets_auto
   */
  async createTransferExtraTokensInstruction(
    wallet: any,
    params: {
      blockhashHash: string | number | bigint;
      extraTokenMint: PublicKey;
      buyerTokenAta?: PublicKey;
      vaultAuthority?: PublicKey;
      vaultAta?: PublicKey;
    },
  ): Promise<TransactionInstruction> {
    if (!wallet?.publicKey) {
      throw new Error(
        '[createTransferExtraTokensInstruction] Wallet not connected',
      );
    }
    if (!params?.blockhashHash) {
      throw new Error(
        '[createTransferExtraTokensInstruction] Missing blockhashHash',
      );
    }
    if (!params?.extraTokenMint) {
      throw new Error(
        '[createTransferExtraTokensInstruction] Missing extraTokenMint',
      );
    }

    const [groupStatePDA] = solanaPDA.getGroupStatePDAForAuto(
      wallet.publicKey,
      params.blockhashHash,
    );

    let vaultAuthority = params.vaultAuthority;
    if (!vaultAuthority) {
      [vaultAuthority] = solanaPDA.getExtraTokenVaultPDA(
        wallet.publicKey,
        params.blockhashHash,
        params.extraTokenMint,
      );
    }

    let buyerTokenAta = params.buyerTokenAta;
    let vaultAta = params.vaultAta;

    if (!buyerTokenAta || !vaultAta) {
      const { getAssociatedTokenAddress } = await import('@solana/spl-token');
      if (!buyerTokenAta) {
        buyerTokenAta = await getAssociatedTokenAddress(
          params.extraTokenMint,
          wallet.publicKey,
        );
      }
      if (!vaultAta) {
        vaultAta = await getAssociatedTokenAddress(
          params.extraTokenMint,
          vaultAuthority,
          true,
        );
      }
    }

    if (!buyerTokenAta || !vaultAta || !vaultAuthority) {
      throw new Error(
        '[createTransferExtraTokensInstruction] Failed to derive SPL accounts',
      );
    }

    const discriminator = Uint8Array.from([183, 209, 0, 223, 185, 35, 72, 104]);

    const keys = [
      { pubkey: groupStatePDA, isSigner: false, isWritable: true },
      { pubkey: wallet.publicKey, isSigner: true, isWritable: true },
      { pubkey: buyerTokenAta, isSigner: false, isWritable: true },
      { pubkey: vaultAuthority, isSigner: false, isWritable: false },
      { pubkey: vaultAta, isSigner: false, isWritable: true },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    ];

    return new TransactionInstruction({
      programId: this.programId,
      keys,
      data: Buffer.from(discriminator),
    });
  }

  /**
   * Create claim lixi instruction
   * @param wallet - Wallet to claim with
   * @param groupId - Group ID to claim from
   * @param opts - Optional SPL token accounts (required if group has extra_token_mint)
   */
  async createClaimLixiInstruction(
    wallet: any,
    groupId: number | bigint,
    groupOptions: {
      blockhashHash: string;
      buyer: PublicKey;
    },
    opts?: {
      extraTokenMint?: PublicKey;
      vaultAuthority?: PublicKey;
      tokenVaultAta?: PublicKey;
      receiverTokenAta?: PublicKey;
    },
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const accounts = buildClaimLixiAccounts({
      wallet: wallet.publicKey,
      groupOptions,
      opts,
    });

    const method = program?.methods?.claimLixi || program?.methods?.claim_lixi;

    if (method) {
      try {
        return await method(groupIdToBN(groupId))
          .accounts(accounts)
          .instruction();
      } catch (encodeError) {
        console.warn(
          '[createClaimLixiInstruction] Anchor encode failed, falling back to manual encoding:',
          encodeError,
        );
      }
    }

    const instructionCoder = new BorshInstructionCoder(idl as any);
    const data = instructionCoder.encode('claim_lixi', {
      group_id: groupIdToBN(groupId),
    } as any);

    const keys = [
      { pubkey: accounts.globalState, isSigner: false, isWritable: true },
      { pubkey: accounts.groupState, isSigner: false, isWritable: true },
      { pubkey: accounts.receiver, isSigner: true, isWritable: true },
      { pubkey: accounts.jackpotVault, isSigner: false, isWritable: true },
      { pubkey: accounts.extraTokenMint, isSigner: false, isWritable: false },
      { pubkey: accounts.vaultAuthority, isSigner: false, isWritable: false },
      { pubkey: accounts.tokenVaultAta, isSigner: false, isWritable: true },
      { pubkey: accounts.receiverTokenAta, isSigner: false, isWritable: true },
      { pubkey: accounts.nftMint, isSigner: false, isWritable: true },
      { pubkey: accounts.nftMetadata, isSigner: false, isWritable: true },
      { pubkey: accounts.nftMasterEdition, isSigner: false, isWritable: true },
      { pubkey: accounts.receiverNftTokenAccount, isSigner: false, isWritable: true },
      { pubkey: accounts.collectionMint, isSigner: false, isWritable: false },
      { pubkey: accounts.collectionMetadata, isSigner: false, isWritable: false },
      { pubkey: accounts.collectionMasterEdition, isSigner: false, isWritable: false },
      { pubkey: accounts.collectionAuthorityRecord, isSigner: false, isWritable: true },
      { pubkey: accounts.tokenMetadataProgram, isSigner: false, isWritable: false },
      { pubkey: accounts.associatedTokenProgram, isSigner: false, isWritable: false },
      { pubkey: accounts.sysvarInstructions, isSigner: false, isWritable: false },
      { pubkey: accounts.tokenProgram, isSigner: false, isWritable: false },
      { pubkey: accounts.systemProgram, isSigner: false, isWritable: false },
    ];

    return new TransactionInstruction({
      programId: this.programId,
      keys,
      data,
    });
  }

  /**
   * Create claim lixi by owner instruction
   * Allows the group owner (buyer) to claim lixi from their own group
   * Supports both native SOL and extra token (SPL) groups
   * @param wallet - Wallet to claim with (must be the group owner/buyer)
   * @param groupId - Group ID to claim from
   * @param groupOptions - Group identifier options (blockhashHash, buyer)
   * @param opts - Optional SPL token accounts (required if group has extra_token_mint)
   */
  async createClaimLixiByOwnerInstruction(
    wallet: any,
    groupId: number | bigint,
    params: {
      receiver: PublicKey;
      groupOptions: {
        blockhashHash: string;
        buyer: PublicKey;
      };
    },
    opts?: {
      extraTokenMint?: PublicKey;
      vaultAuthority?: PublicKey;
      tokenVaultAta?: PublicKey;
      receiverTokenAta?: PublicKey;
    },
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const accounts = buildClaimLixiByOwnerAccounts({
      wallet: wallet.publicKey,
      receiver: params.receiver,
      groupOptions: params.groupOptions,
      opts,
    });

    const instructionCoder = new BorshInstructionCoder(idl as any);
    const data = instructionCoder.encode('claim_lixi_by_owner', {
      group_id: groupIdToBN(groupId),
    } as any);

    const keys = [
      { pubkey: accounts.globalState, isSigner: false, isWritable: true },
      { pubkey: accounts.groupState, isSigner: false, isWritable: true },
      { pubkey: accounts.owner, isSigner: true, isWritable: true },
      { pubkey: accounts.receiver, isSigner: false, isWritable: true },
      { pubkey: accounts.jackpotVault, isSigner: false, isWritable: true },
      { pubkey: accounts.extraTokenMint, isSigner: false, isWritable: false },
      { pubkey: accounts.vaultAuthority, isSigner: false, isWritable: false },
      { pubkey: accounts.tokenVaultAta, isSigner: false, isWritable: true },
      { pubkey: accounts.receiverTokenAta, isSigner: false, isWritable: true },
      { pubkey: accounts.nftMint, isSigner: false, isWritable: true },
      { pubkey: accounts.nftMetadata, isSigner: false, isWritable: true },
      { pubkey: accounts.nftMasterEdition, isSigner: false, isWritable: true },
      { pubkey: accounts.receiverNftTokenAccount, isSigner: false, isWritable: true },
      { pubkey: accounts.collectionMint, isSigner: false, isWritable: false },
      { pubkey: accounts.collectionMetadata, isSigner: false, isWritable: false },
      { pubkey: accounts.collectionMasterEdition, isSigner: false, isWritable: false },
      { pubkey: accounts.collectionAuthorityRecord, isSigner: false, isWritable: true },
      { pubkey: accounts.tokenMetadataProgram, isSigner: false, isWritable: false },
      { pubkey: accounts.tokenProgram, isSigner: false, isWritable: false },
      { pubkey: accounts.associatedTokenProgram, isSigner: false, isWritable: false },
      { pubkey: accounts.systemProgram, isSigner: false, isWritable: false },
      { pubkey: accounts.sysvarInstructions, isSigner: false, isWritable: false },
    ];

    return new TransactionInstruction({
      programId: this.programId,
      keys,
      data,
    });
  }

  /**
   * Create mint_nft instruction
   * Mints NFT for a specific ticket after buy_tickets_auto
   * @param wallet - Admin wallet (must be global state admin)
   * @param params - Ticket ID and receiver
   */
  async createMintNftInstruction(
    wallet: any,
    params: {
      ticketId: number | bigint;
      receiver: PublicKey;
      groupOptions: {
        blockhashHash: string;
        buyer: PublicKey;
      };
    },
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const connection = this.connection;

    // Get global state to get collection mint and admin
    const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
    const globalState = await this.getGlobalState();
    if (!globalState) {
      throw new Error('Global state not found');
    }

    const collectionMintPDA =
      globalState.collectionMint ||
      globalState.collection_mint ||
      PublicKey.default;
    if (collectionMintPDA.equals(PublicKey.default)) {
      throw new Error('Collection mint not found in global state');
    }
    const collectionMint =
      collectionMintPDA instanceof PublicKey
        ? collectionMintPDA
        : new PublicKey(collectionMintPDA.toString());

    // Derive NFT mint PDA: [b"nft_mint", ticket_id]
    const [nftMintPDA] = solanaPDA.getNFTMintPDA(
      typeof params.ticketId === 'bigint'
        ? Number(params.ticketId)
        : params.ticketId,
    );

    // Derive NFT metadata PDA
    const [nftMetadataPDA] = PublicKey.findProgramAddressSync(
      [
        Buffer.from('metadata'),
        TOKEN_METADATA_PROGRAM_ID.toBuffer(),
        nftMintPDA.toBuffer(),
      ],
      TOKEN_METADATA_PROGRAM_ID,
    );

    // Derive NFT master edition PDA
    const [nftMasterEditionPDA] = PublicKey.findProgramAddressSync(
      [
        Buffer.from('metadata'),
        TOKEN_METADATA_PROGRAM_ID.toBuffer(),
        nftMintPDA.toBuffer(),
        Buffer.from('edition'),
      ],
      TOKEN_METADATA_PROGRAM_ID,
    );

    // Derive receiver token account (ATA)
    const { getAssociatedTokenAddress } = await import('@solana/spl-token');
    const receiverTokenAccount = await getAssociatedTokenAddress(
      nftMintPDA,
      params.receiver,
    );

    // Get collection accounts
    const collectionMetadata = getCollectionMetadataPDA(collectionMint);
    const collectionMasterEdition =
      getCollectionMasterEditionPDA(collectionMint);
    const collectionAuthorityRecord = getCollectionAuthorityRecordPDA(
      collectionMint,
      wallet.publicKey,
    );

    // Derive group state PDA for authority validation
    const [groupStatePDA] = solanaPDA.getGroupStatePDAForAuto(
      params.groupOptions.buyer,
      params.groupOptions.blockhashHash,
    );

    // Get sysvar instructions
    const SYSVAR_INSTRUCTIONS_PUBKEY = new PublicKey(
      'Sysvar1nstructions1111111111111111111111111',
    );

    // Use Anchor program methods if available
    if (program?.methods?.mintNft || program?.methods?.mint_nft) {
      const method = program.methods.mintNft || program.methods.mint_nft;
      try {
        return await method(groupIdToBN(params.ticketId), params.receiver)
          .accounts({
            globalState: globalStatePDA,
            authority: wallet.publicKey,
            groupState: groupStatePDA,
            receiver: params.receiver,
            nftMint: nftMintPDA,
            nftMetadata: nftMetadataPDA,
            nftMasterEdition: nftMasterEditionPDA,
            receiverTokenAccount,
            collectionMint,
            collectionMetadata,
            collectionMasterEdition,
            collectionAuthorityRecord,
            tokenMetadataProgram: TOKEN_METADATA_PROGRAM_ID,
            tokenProgram: TOKEN_PROGRAM_ID,
            associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
            systemProgram: SystemProgram.programId,
            sysvarInstructions: SYSVAR_INSTRUCTIONS_PUBKEY,
          })
          .instruction();
      } catch (encodeError) {
        console.warn(
          '[createMintNftInstruction] Anchor encode failed, falling back to manual encoding:',
          encodeError,
        );
      }
    } else {
      console.warn(
        '[createMintNftInstruction] mintNft not found in program.methods, falling back to manual encoding',
      );
    }

    // Fallback: Manual instruction creation using BorshInstructionCoder
    const idlForProgram = program.idl as any;
    const mintNftMethod = idlForProgram.instructions.find(
      (ix: any) => ix.name === 'mint_nft' || ix.name === 'mintNft',
    );

    if (!mintNftMethod) {
      throw new Error('mint_nft instruction not found in IDL');
    }

    const instructionCoder = new BorshInstructionCoder(idlForProgram);
    const data = instructionCoder.encode(mintNftMethod.name, {
      ticketId: groupIdToBN(params.ticketId),
      receiver: params.receiver,
    });

    if (!data) {
      throw new Error('Failed to encode mint_nft instruction data');
    }

    // Build accounts array in IDL order
    const keys = [
      {
        pubkey: globalStatePDA,
        isSigner: false,
        isWritable: true,
      },
      {
        pubkey: wallet.publicKey,
        isSigner: true,
        isWritable: false,
      },
      {
        pubkey: groupStatePDA,
        isSigner: false,
        isWritable: true,
      },
      {
        pubkey: params.receiver,
        isSigner: false,
        isWritable: false,
      },
      {
        pubkey: nftMintPDA,
        isSigner: false,
        isWritable: true,
      },
      {
        pubkey: nftMetadataPDA,
        isSigner: false,
        isWritable: true,
      },
      {
        pubkey: nftMasterEditionPDA,
        isSigner: false,
        isWritable: true,
      },
      {
        pubkey: receiverTokenAccount,
        isSigner: false,
        isWritable: true,
      },
      {
        pubkey: collectionMint,
        isSigner: false,
        isWritable: true,
      },
      {
        pubkey: collectionMetadata,
        isSigner: false,
        isWritable: true,
      },
      {
        pubkey: collectionMasterEdition,
        isSigner: false,
        isWritable: true,
      },
      {
        pubkey: collectionAuthorityRecord,
        isSigner: false,
        isWritable: true,
      },
      {
        pubkey: TOKEN_METADATA_PROGRAM_ID,
        isSigner: false,
        isWritable: false,
      },
      {
        pubkey: TOKEN_PROGRAM_ID,
        isSigner: false,
        isWritable: false,
      },
      {
        pubkey: ASSOCIATED_TOKEN_PROGRAM_ID,
        isSigner: false,
        isWritable: false,
      },
      {
        pubkey: SystemProgram.programId,
        isSigner: false,
        isWritable: false,
      },
      {
        pubkey: SYSVAR_INSTRUCTIONS_PUBKEY,
        isSigner: false,
        isWritable: false,
      },
    ];

    return new TransactionInstruction({
      programId: this.programId,
      keys,
      data,
    });
  }

  /**
   * Create claim global jackpot instruction
   * Claims global jackpot for the winner who owns the winning NFT
   * Requires: winningHash set, totalTickets > 0, closingTime passed, global_jackpot_claimed = false
   * @param wallet - Wallet to claim with (must own the winning NFT)
   * @param opts - Required accounts: purchaser, winningNftMint, winnerNftTokenAccount
   */
  async createClaimGlobalJackpotInstruction(
    wallet: any,
    opts: {
      purchaser: PublicKey;
      winningNftMint: PublicKey;
      winnerNftTokenAccount: PublicKey;
      groupState?: PublicKey;
      groupOptions?: {
        buyer: PublicKey;
        blockhashHash: BlockhashHash;
      };
    },
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const accounts = buildClaimGlobalJackpotAccounts({
      wallet: wallet.publicKey,
      purchaser: opts.purchaser,
      winningNftMint: opts.winningNftMint,
      winnerNftTokenAccount: opts.winnerNftTokenAccount,
      groupState: opts.groupState,
      groupOptions: opts.groupOptions,
    });

    const method =
      program?.methods?.claimGlobalJackpot ||
      program?.methods?.claim_global_jackpot;

    if (!method) {
      throw new Error(
        `claimGlobalJackpot is not supported in current IDL. Available methods: ${Object.keys(
          program?.methods || {},
        ).join(', ')}`,
      );
    }

    return await method().accounts(accounts).instruction();
  }

  /**
   * Create claim local jackpot instruction
   * Claims local jackpot (extra token prize) for a group
   * Requires: group must have extra_token_mint, local_prize_amount > 0, winning_hash set, closing_time passed
   * @param wallet - Wallet to claim with (must own the winning NFT)
   * @param groupId - Group ID to claim from
   * @param groupOptions - Group identifier options (blockhashHash, buyer)
   * @param opts - Required SPL token accounts (group must have extra_token_mint)
   */
  async createClaimLocalJackpotInstruction(
    wallet: any,
    groupId: number | bigint,
    groupOptions: {
      blockhashHash: string;
      buyer: PublicKey;
    },
    opts: {
      extraTokenMint: PublicKey;
      vaultAuthority: PublicKey;
      tokenVaultAta: PublicKey;
      winnerTokenAta: PublicKey;
      winningNftMint: PublicKey;
      winnerNftTokenAccount: PublicKey;
    },
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const accounts = buildClaimLocalJackpotAccounts({
      wallet: wallet.publicKey,
      groupOptions,
      opts,
    });

    const method =
      program?.methods?.claimLocalJackpot ||
      program?.methods?.claim_local_jackpot;

    if (!method) {
      throw new Error(
        `claimLocalJackpot is not supported in current IDL. Available methods: ${Object.keys(
          program?.methods || {},
        ).join(', ')}`,
      );
    }

    return await method(groupIdToBN(groupId)).accounts(accounts).instruction();
  }

  /**
   * Create claim many by owner instruction
   * Allows group owner to claim lixi for multiple receivers in a single transaction
   * Supports up to 5 receivers
   * Requires: closingTime not passed, enough tickets available
   * @param wallet - Wallet to claim with (must be the group owner)
   * @param groupId - Group ID to claim from
   * @param numReceivers - Number of receivers to claim for (1-5)
   * @param receivers - Array of receiver public keys (up to 5)
   * @param groupOptions - Group identifier options (blockhashHash, buyer)
   * @param opts - Optional SPL token accounts (required if group has extra_token_mint)
   */
  async createClaimManyByOwnerInstruction(
    wallet: any,
    groupId: number | bigint,
    numReceivers: number,
    receivers: PublicKey[],
    groupOptions: {
      blockhashHash: string;
      buyer: PublicKey;
    },
    opts?: {
      extraTokenMint?: PublicKey;
      vaultAuthority?: PublicKey;
      tokenVaultAta?: PublicKey;
      receiverTokenAtas?: PublicKey[];
      ticketIds?: number[];
      nftMints?: PublicKey[];
      nftMetadatas?: PublicKey[];
      nftMasterEditions?: PublicKey[];
      receiverNftTokenAccounts?: PublicKey[];
      collectionMint?: PublicKey;
      admin?: PublicKey;
    },
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const accounts = buildClaimManyByOwnerAccounts({
      wallet: wallet.publicKey,
      groupOptions,
      receivers,
      numReceivers,
      opts,
    });

    const method =
      program?.methods?.claimManyByOwner ||
      program?.methods?.claim_many_by_owner;

    if (!method) {
      throw new Error(
        `claimManyByOwner is not supported in current IDL. Available methods: ${Object.keys(
          program?.methods || {},
        ).join(', ')}`,
      );
    }

    return await method(groupIdToBN(groupId), numReceivers)
      .accounts(accounts)
      .instruction();
  }

  /**
   * Create claim many by admin instruction
   * Allows admin to claim lixi for multiple receivers in a single transaction
   * Supports up to 5 receivers
   * Requires: closingTime not passed, enough tickets available
   * @param wallet - Admin wallet
   * @param groupId - Group ID to claim from
   * @param numReceivers - Number of receivers to claim for (1-5)
   * @param receivers - Array of receiver public keys (up to 5)
   * @param groupOptions - Group identifier options (blockhashHash, buyer)
   * @param opts - Optional SPL token accounts (required if group has extra_token_mint)
   */
  async createClaimManyByAdminInstruction(
    wallet: any,
    groupId: number | bigint,
    numReceivers: number,
    receivers: PublicKey[],
    groupOptions: {
      blockhashHash: string;
      buyer: PublicKey;
    },
    opts?: {
      extraTokenMint?: PublicKey;
      vaultAuthority?: PublicKey;
      tokenVaultAta?: PublicKey;
      receiverTokenAtas?: PublicKey[];
      ticketIds?: number[];
      nftMints?: PublicKey[];
      nftMetadatas?: PublicKey[];
      nftMasterEditions?: PublicKey[];
      receiverNftTokenAccounts?: PublicKey[];
      collectionMint?: PublicKey;
      admin?: PublicKey;
    },
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const accounts = buildClaimManyByAdminAccounts({
      wallet: wallet.publicKey,
      groupOptions,
      receivers,
      numReceivers,
      opts,
    });

    const method =
      program?.methods?.claimManyByAdmin ||
      program?.methods?.claim_many_by_admin;

    if (!method) {
      throw new Error(
        `claimManyByAdmin is not supported in current IDL. Available methods: ${Object.keys(
          program?.methods || {},
        ).join(', ')}`,
      );
    }

    return await method(groupIdToBN(groupId), numReceivers)
      .accounts(accounts)
      .instruction();
  }

  /**
   * Create claim lixi by admin instruction
   * Allows admin to claim lixi for a receiver
   * Supports both native SOL and extra token (SPL) groups
   * @param wallet - Admin wallet
   * @param groupId - Group ID to claim from
   * @param receiver - Receiver public key
   * @param groupOptions - Group identifier options (blockhashHash, buyer)
   * @param opts - Optional SPL token accounts (required if group has extra_token_mint)
   */
  async createClaimLixiByAdminInstruction(
    wallet: any,
    groupId: number | bigint,
    receiver: PublicKey,
    groupOptions: {
      blockhashHash: string;
      buyer: PublicKey;
    },
    opts?: {
      extraTokenMint?: PublicKey;
      vaultAuthority?: PublicKey;
      tokenVaultAta?: PublicKey;
      receiverTokenAta?: PublicKey;
      ticketId?: number;
      nftMint?: PublicKey;
      nftMetadata?: PublicKey;
      nftMasterEdition?: PublicKey;
      receiverNftTokenAccount?: PublicKey;
      collectionMint?: PublicKey;
      admin?: PublicKey;
    },
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const accounts = buildClaimLixiByAdminAccounts({
      wallet: wallet.publicKey,
      receiver,
      groupOptions,
      opts,
    });

    const method =
      program?.methods?.claimLixiByAdmin ||
      program?.methods?.claim_lixi_by_admin;

    if (method) {
      try {
        return await method(groupIdToBN(groupId))
          .accounts(accounts)
          .instruction();
      } catch (encodeError) {
        console.warn(
          '[createClaimLixiByAdminInstruction] Anchor encode failed, falling back to manual encoding:',
          encodeError,
        );
      }
    }

    const instructionCoder = new BorshInstructionCoder(idl as any);
    const data = instructionCoder.encode('claim_lixi_by_admin', {
      group_id: groupIdToBN(groupId),
    } as any);

    const keys = [
      { pubkey: accounts.globalState, isSigner: false, isWritable: true },
      { pubkey: accounts.groupState, isSigner: false, isWritable: true },
      { pubkey: accounts.admin, isSigner: true, isWritable: true },
      { pubkey: accounts.receiver, isSigner: false, isWritable: true },
      { pubkey: accounts.jackpotVault, isSigner: false, isWritable: true },
      { pubkey: accounts.extraTokenMint, isSigner: false, isWritable: false },
      { pubkey: accounts.vaultAuthority, isSigner: false, isWritable: false },
      { pubkey: accounts.tokenVaultAta, isSigner: false, isWritable: true },
      { pubkey: accounts.receiverTokenAta, isSigner: false, isWritable: true },
      { pubkey: accounts.nftMint, isSigner: false, isWritable: true },
      { pubkey: accounts.nftMetadata, isSigner: false, isWritable: true },
      { pubkey: accounts.nftMasterEdition, isSigner: false, isWritable: true },
      { pubkey: accounts.receiverNftTokenAccount, isSigner: false, isWritable: true },
      { pubkey: accounts.collectionMint, isSigner: false, isWritable: false },
      { pubkey: accounts.collectionMetadata, isSigner: false, isWritable: false },
      { pubkey: accounts.collectionMasterEdition, isSigner: false, isWritable: false },
      { pubkey: accounts.collectionAuthorityRecord, isSigner: false, isWritable: true },
      { pubkey: accounts.tokenMetadataProgram, isSigner: false, isWritable: false },
      { pubkey: accounts.tokenProgram, isSigner: false, isWritable: false },
      { pubkey: accounts.associatedTokenProgram, isSigner: false, isWritable: false },
      { pubkey: accounts.systemProgram, isSigner: false, isWritable: false },
      { pubkey: accounts.sysvarInstructions, isSigner: false, isWritable: false },
    ];

    return new TransactionInstruction({
      programId: this.programId,
      keys,
      data,
    });
  }

  /**
   * Create claim fund back instruction
   * Allows group owner to claim remaining funds after closing time
   * Requires: closingTime passed, remaining tickets > 0
   * @param wallet - Wallet to claim with (must be the group owner)
   * @param groupId - Group ID to claim from
   * @param groupOptions - Group identifier options (blockhashHash, buyer)
   */
  async createClaimFundBackInstruction(
    wallet: any,
    groupId: number | bigint,
    groupOptions: {
      blockhashHash: string;
      buyer: PublicKey;
    },
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const accounts = buildClaimFundBackAccounts({
      wallet: wallet.publicKey,
      groupOptions,
    });

    const method =
      program?.methods?.claimFundBack || program?.methods?.claim_fund_back;

    if (!method) {
      throw new Error(
        `claimFundBack is not supported in current IDL. Available methods: ${Object.keys(
          program?.methods || {},
        ).join(', ')}`,
      );
    }

    return await method(groupIdToBN(groupId)).accounts(accounts).instruction();
  }

  /**
   * Create add whitelister instruction
   */
  async createAddWhitelisterInstruction(
    wallet: any,
    groupId: number | bigint,
    whitelister: PublicKey,
    options: {
      blockhashHash: string; // buy_tickets_auto PDA pattern
      buyer: PublicKey; // Buyer/creator address for buy_tickets_auto pattern
    },
  ): Promise<TransactionInstruction> {
    const accountsObj = buildAddWhitelisterAccounts({
      wallet: wallet.publicKey,
      groupOptions: options,
    });

    // PRIORITY 1: Try using Anchor Program methods (same as other instructions)
    // This is the standard way and should work if Program can be created
    let programError: any = null;
    try {
      const program = this.getProgramWithWallet(wallet);

      // Check if method exists (camelCase or snake_case)
      if (
        !program?.methods?.addWhitelister &&
        !program?.methods?.add_whitelister
      ) {
        throw new Error(
          `addWhitelister is not supported in current IDL. Available methods: ${Object.keys(
            program?.methods || {},
          ).join(', ')}`,
        );
      }

      const method =
        program.methods.addWhitelister || program.methods.add_whitelister;

      if (!method) {
        throw new Error('Method addWhitelister/add_whitelister is undefined');
      }

      // Prepare accounts object - use camelCase per Anchor js mapping
      // Anchor automatically converts IDL snake_case to camelCase in JS
      console.log(
        '[createAddWhitelisterInstruction] Using Anchor Program methods',
      );
    console.log('[createAddWhitelisterInstruction] Accounts object:', {
      globalState: accountsObj.globalState?.toString(),
      groupState: accountsObj.groupState?.toString(),
      authority: accountsObj.authority?.toString(),
    });

      // Call method with group_id and whitelister arguments
      // IDL has group_id in args after Rust code fix
      const whitelisterKey =
        whitelister instanceof PublicKey
          ? whitelister
          : new PublicKey(whitelister);

      const methodCall = method(groupIdToBN(groupId), whitelisterKey);

      if (!methodCall || typeof methodCall.accounts !== 'function') {
        throw new Error(
          `Method call result is invalid or missing accounts function`,
        );
      }

      const instruction = await methodCall.accounts(accountsObj).instruction();

      console.log(
        '[createAddWhitelisterInstruction] ✅ Created instruction using Anchor Program methods',
      );
      return instruction;
    } catch (error: any) {
      programError = error;
      console.warn(
        '[createAddWhitelisterInstruction] ⚠️ Program method failed, trying manual BorshInstructionCoder:',
        programError?.message || programError,
      );
    }

    // PRIORITY 2: Fallback - Create instruction manually using BorshInstructionCoder
    const instructionCoder = new BorshInstructionCoder(idl as any);
    const idlMethod = (idl as any)?.instructions?.find(
      (ix: any) =>
        ix.name === 'add_whitelister' || ix.name === 'addWhitelister',
    );

    if (idlMethod && instructionCoder) {
      try {
        // Encode instruction data manually
        const instructionData = instructionCoder.encode('add_whitelister', {
          group_id: groupIdToBN(groupId),
          whitelister: whitelister,
        });

        if (instructionData) {
          // Build account keys array from IDL account order
          const accountNameMap: { [key: string]: string } = {
            global_state: 'globalState',
            group_state: 'groupState',
            admin: 'authority',
            authority: 'authority',
          };

          const keys = idlMethod.accounts.map((acc: any, idx: number) => {
            const camelCaseName = accountNameMap[acc.name] || acc.name;
            const accountKey =
              accountsObj[camelCaseName as keyof typeof accountsObj];
            if (!accountKey) {
              throw new Error(
                `Account ${acc.name} (${camelCaseName}) not found`,
              );
            }
            const key = {
              pubkey:
                accountKey instanceof PublicKey
                  ? accountKey
                  : new PublicKey(accountKey),
              isSigner: acc.signer || false,
              isWritable: acc.writable || false,
            };
            // Debug: Log each account key with IDL comparison
            console.log(
              `[createAddWhitelisterInstruction] Account ${idx} (${acc.name}):`,
              {
                pubkey: key.pubkey.toString(),
                isSigner: key.isSigner,
                isWritable: key.isWritable,
                idlSigner: acc.signer || false,
                idlWritable: acc.writable || false,
                match:
                  key.isSigner === (acc.signer || false) &&
                  key.isWritable === (acc.writable || false),
              },
            );
            return key;
          });

          console.log(
            '[createAddWhitelisterInstruction] ✅ Created instruction manually using BorshInstructionCoder (fallback)',
          );
          console.log(
            '[createAddWhitelisterInstruction] IDL account order:',
            idlMethod.accounts.map((acc: any, idx: number) => ({
              index: idx,
              name: acc.name,
              signer: acc.signer || false,
              writable: acc.writable || false,
            })),
          );
          return new TransactionInstruction({
            programId: new PublicKey(SOLANA_PROGRAM_ID),
            keys,
            data: instructionData,
          });
        } else {
          throw new Error(
            'BorshInstructionCoder.encode returned null/undefined',
          );
        }
      } catch (manualError: any) {
        console.error(
          '[createAddWhitelisterInstruction] ❌ Manual instruction creation also failed:',
          manualError?.message || manualError,
        );
        throw new Error(
          `Failed to create instruction: Program method error: ${
            programError?.message || 'Unknown'
          }, ` + `Manual method error: ${manualError?.message || 'Unknown'}`,
        );
      }
    } else {
      throw new Error(
        `Cannot create instruction: Program method failed: ${
          programError?.message || 'Unknown'
        }, ` +
          `and manual method unavailable (missing idlMethod or instructionCoder)`,
      );
    }
  }

  /**
   * Create remove whitelister instruction
   */
  async createRemoveWhitelisterInstruction(
    wallet: any,
    groupId: number | bigint,
    whitelister: PublicKey,
    groupOptions: {
      blockhashHash: string;
      buyer: PublicKey;
    },
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const accounts = buildRemoveWhitelisterAccounts({
      wallet: wallet.publicKey,
      groupOptions,
    });

    if (
      !program?.methods?.removeWhitelister &&
      !program?.methods?.remove_whitelister
    ) {
      throw new Error('removeWhitelister is not supported in current IDL');
    }

    const m =
      program.methods.removeWhitelister || program.methods.remove_whitelister;
    return await m(groupIdToBN(groupId), whitelister)
      .accounts(accounts)
      .instruction();
  }

  /**
   * Create add many whitelisters instruction
   */
  async createAddManyWhitelistersInstruction(
    wallet: any,
    groupId: number | bigint,
    whitelisters: PublicKey[],
    groupOptions: {
      blockhashHash: string;
      buyer: PublicKey;
    },
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const accounts = buildAddManyWhitelistersAccounts({
      wallet: wallet.publicKey,
      groupOptions,
    });

    if (
      !program?.methods?.addManyWhitelisters &&
      !program?.methods?.add_many_whitelisters
    ) {
      throw new Error('addManyWhitelisters is not supported in current IDL');
    }

    const m =
      program.methods.addManyWhitelisters ||
      program.methods.add_many_whitelisters;
    return await m(groupIdToBN(groupId), whitelisters)
      .accounts(accounts)
      .instruction();
  }

  /**
   * Create remove many whitelisters instruction
   * Removes multiple whitelisters from a group in a single transaction
   * @param wallet - Admin wallet
   * @param groupId - Group ID
   * @param whitelisters - Array of whitelister public keys to remove (max 50)
   * @param groupOptions - Group identifier options (blockhashHash, buyer)
   */
  async createRemoveManyWhitelistersInstruction(
    wallet: any,
    groupId: number | bigint,
    whitelisters: PublicKey[],
    groupOptions: {
      blockhashHash: string;
      buyer: PublicKey;
    },
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const accounts = buildRemoveManyWhitelistersAccounts({
      wallet: wallet.publicKey,
      groupOptions,
    });

    // Validate batch size (max 50 per Rust code)
    if (whitelisters.length > 50) {
      throw new Error('Cannot remove more than 50 whitelisters at once');
    }

    if (
      !program?.methods?.removeManyWhitelisters &&
      !program?.methods?.remove_many_whitelisters
    ) {
      throw new Error('removeManyWhitelisters is not supported in current IDL');
    }

    const m =
      program.methods.removeManyWhitelisters ||
      program.methods.remove_many_whitelisters;
    return await m(groupIdToBN(groupId), whitelisters)
      .accounts(accounts)
      .instruction();
  }

  /**
   * Config instructions: set ticket price, closing time, winning hash
   */
  async createSetTicketPriceInstruction(
    wallet: any,
    newPriceLamports: number,
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
    const method =
      program?.methods?.setTicketPrice || program?.methods?.set_ticket_price;
    if (!method)
      throw new Error('setTicketPrice is not supported in current IDL');
    return await method(new BN(newPriceLamports))
      .accounts({
        globalState: globalStatePDA,
        admin: wallet.publicKey,
        systemProgram: PublicKey.default,
      })
      .instruction();
  }

  async createSetClosingTimeInstruction(
    wallet: any,
    newClosingTime: number,
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
    const method =
      program?.methods?.setClosingTime || program?.methods?.set_closing_time;
    if (!method)
      throw new Error('setClosingTime is not supported in current IDL');
    return await method(new BN(newClosingTime))
      .accounts({
        globalState: globalStatePDA,
        admin: wallet.publicKey,
        systemProgram: PublicKey.default,
      })
      .instruction();
  }

  async createSetWinningHashInstruction(
    wallet: any,
    winningHash: Uint8Array,
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
    const method =
      program?.methods?.setWinningHash || program?.methods?.set_winning_hash;
    if (!method)
      throw new Error('setWinningHash is not supported in current IDL');
    // Ensure 32 bytes array
    const hash32 = winningHash.length === 32 ? winningHash : new Uint8Array(32);
    return await method(Array.from(hash32))
      .accounts({
        globalState: globalStatePDA,
        admin: wallet.publicKey,
        systemProgram: PublicKey.default,
      })
      .instruction();
  }

  /**
   * Update admin status
   * Mapping: BNB setAdminStatus -> Solana update_admin(account, is_admin)
   */
  async createUpdateAdminInstruction(
    wallet: any,
    account: PublicKey,
    isAdmin: boolean,
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
    const method =
      program?.methods?.updateAdmin || program?.methods?.update_admin;
    if (!method) throw new Error('updateAdmin is not supported in current IDL');
    return await method(account, isAdmin)
      .accounts({
        globalState: globalStatePDA,
        admin: wallet.publicKey,
        systemProgram: PublicKey.default,
      })
      .instruction();
  }

  /**
   * Set jackpot config
   * Expected config fields in bps: localJackpotPercentBps, globalJackpotPercentBps, globalJackpotCommissionBps, globalJackpotServiceFeeBps
   */
  async createSetJackpotConfigInstruction(
    wallet: any,
    config: {
      localJackpotPercentBps: number;
      globalJackpotPercentBps: number;
      globalJackpotCommissionBps: number;
      globalJackpotServiceFeeBps: number;
    },
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
    const method =
      program?.methods?.setJackpotConfig ||
      program?.methods?.set_jackpot_config;
    if (!method)
      throw new Error('setJackpotConfig is not supported in current IDL');
    return await method(
      new BN(config.localJackpotPercentBps),
      new BN(config.globalJackpotPercentBps),
      new BN(config.globalJackpotCommissionBps),
      new BN(config.globalJackpotServiceFeeBps),
    )
      .accounts({
        globalState: globalStatePDA,
        admin: wallet.publicKey,
        systemProgram: PublicKey.default,
      })
      .instruction();
  }

  /**
   * Create instruction to initialize or fix a group state account
   * This can handle both new accounts (init) and existing accounts (fix corrupted data)
   * Admin only
   *
   * NOTE: This method uses the deprecated `getGroupStatePDA(groupId)` pattern.
   * This is kept for backward compatibility with old groups that may need fixing.
   * For new groups, use buy_tickets_auto which creates groups with the new PDA pattern.
   */
  async createInitOrFixGroupStateInstruction(
    wallet: any,
    groupId: number | bigint,
  ): Promise<TransactionInstruction> {
    const program = this.getProgramWithWallet(wallet);
    const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
    // NOTE: Using deprecated getGroupStatePDA for backward compatibility with old groups
    const [groupStatePDA] = solanaPDA.getGroupStatePDA(groupId);

    // Check if method exists (camelCase or snake_case)
    const method =
      program.methods.initOrFixGroupState ||
      program.methods.init_or_fix_group_state;

    if (!method) {
      throw new Error(
        'initOrFixGroupState is not supported in current IDL. Available methods: ' +
          Object.keys(program?.methods || {}).join(', '),
      );
    }

    return await method(new BN(groupId))
      .accounts({
        global_state: globalStatePDA,
        group_state: groupStatePDA,
        owner: wallet.publicKey,
        system_program: SystemProgram.programId,
      })
      .instruction();
  }
}

// Export singleton instance
export const solanaProgramClient = new SolanaProgramClient();
